import React, { useState, useEffect, ReactNode } from 'react';
import Stack from '@mui/material/Stack';
import { Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TwTooltip from '../TwTooltip/TwTooltip';
import { InfoIcon } from './TwGrossPayTooltip.styles';
import { LOAD_TYPE } from '../../redux/api/load/load.model';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import { TRIP_DRIVER_PAY_UNIT, TripDriverOverrideData } from '../../redux/api/trips/trips.model';
import useAuth from '../../providers/Auth/Auth.provider';

export interface TwGrossPayTooltipProps {
  grossPayData: TripDriverOverrideData;
  showLabel?: boolean;
  loadType: LOAD_TYPE;
  numberOfDrivers: number;
  otherPayItemsTotalAmount: number;
}

const TwGrossPayTooltip: React.FC<TwGrossPayTooltipProps> = ({
  grossPayData,
  showLabel,
  loadType,
  numberOfDrivers,
  otherPayItemsTotalAmount
}) => {
  const {
    data: { userData }
  } = useAuth();
  const { enableOtherPayInDriverPayModal } = useFeatureFlags();
  const enableLtl = userData?.tenant?.ltl_enabled || false;

  const getTooltipText = (): ReactNode => {
    switch (grossPayData.unit) {
      //  case PAY_TYPES.PER_MILE:
      case TRIP_DRIVER_PAY_UNIT.PER_MILE:
        return `Driver pay estimated based on driver's per mi rate: $${
          grossPayData.rate
        } per mi x (${grossPayData?.loaded_miles} loaded mi + ${
          grossPayData?.empty_miles || 0
        } empty mi) ${
          enableOtherPayInDriverPayModal ? `+ $${otherPayItemsTotalAmount} other trip pay` : ''
        }`;
        break;

      // case PAY_TYPES.PERCENTAGE:
      case TRIP_DRIVER_PAY_UNIT.PERCENTAGE:
        if (loadType === LOAD_TYPE.SEGMENT) {
          return `Driver pay estimated based on driver's percentage rate: ${
            grossPayData.rate || 0
          }% x ((${(grossPayData.trip_revenue_percentage || 0) * 100}% x ${
            grossPayData.overriden_order_rate
          }) / ${numberOfDrivers} driver(s)) ${
            enableOtherPayInDriverPayModal ? `+ $${otherPayItemsTotalAmount} other trip pay` : ''
          }`;
        } else {
          const text = `Driver pay estimated based on driver's percentage rate: ${Number(
            grossPayData.rate
          )}% x (${`$${grossPayData?.overriden_order_rate}`} / ${numberOfDrivers} driver(s)) ${
            enableOtherPayInDriverPayModal ? `+ $${otherPayItemsTotalAmount} other trip pay` : ''
          }`;

          let ordersRatesInfo = '';
          if (
            grossPayData.orders_rate_totals &&
            grossPayData.orders_rate_totals?.length > 0 &&
            enableLtl
          ) {
            ordersRatesInfo += `\n\nOrders rate(s): ${grossPayData?.orders_rate_totals
              .map((order) => `Order #${order.readable_id} ($${order.subtotal}) `)
              .join('+ ')}`;
          }

          return (
            <>
              <Typography variant="body2" fontSize={'12px'}>
                {text}
              </Typography>
              {ordersRatesInfo !== '' && (
                <Typography variant="body2" fontSize={'12px'} sx={{ marginTop: '10px' }}>
                  {ordersRatesInfo}
                </Typography>
              )}
            </>
          );
        }

      // case PAY_TYPES.PER_HOUR:
      case TRIP_DRIVER_PAY_UNIT.PER_HOUR:
        return `Driver pay estimated based on driver's hourly rate: $${
          grossPayData.rate
        } per hr x ${grossPayData.quantity} hours ${
          enableOtherPayInDriverPayModal ? `+ $${otherPayItemsTotalAmount} other trip pay` : ''
        }`;

      case TRIP_DRIVER_PAY_UNIT.FLAT:
      case 'FLAT':
        return `Driver pay estimated based on flat rate: $${grossPayData.gross_pay_estimate} ${
          enableOtherPayInDriverPayModal ? `+ $${otherPayItemsTotalAmount} other trip pay` : ''
        }`;

      default:
        <></>;
    }
  };

  return (
    <Stack spacing={0.5} direction="row" alignItems="center">
      <TwTooltip title={getTooltipText()} placement="top">
        {showLabel ? <InfoIcon /> : <InfoOutlinedIcon sx={{ height: '16px' }} />}
      </TwTooltip>
      {showLabel && (
        <Typography
          variant="body2"
          fontWeight="400"
          fontSize="14px"
          color="#6B7280"
          sx={{ marginTop: '20px !important' }}
        >
          How it’s calculated?
        </Typography>
      )}
    </Stack>
  );
};

export default TwGrossPayTooltip;
